/**
 * ExportPlaylistModal
 * Allows users to export discovered tracks as a playlist on Spotify or Apple Music
 */

import { useState } from 'react';
import { usePlaylistExportStore } from '../stores/playlist-export.store';
import { useExploreStore } from '../stores/explore-store';
import type { VibeControls, ConstellationNode } from '../types/explore.types';

function generatePlaylistName(vibe: VibeControls, nodes: ConstellationNode[]): string {
  // Pick a tone word from temperature, then groove, then density
  let toneWord = '';
  if (vibe.temperature >= 0.7) toneWord = 'warm';
  else if (vibe.temperature <= 0.3) toneWord = 'cold';
  else if (vibe.groove >= 0.75) toneWord = 'groovy';
  else if (vibe.groove <= 0.25) toneWord = 'mechanical';
  else if (vibe.density >= 0.75) toneWord = 'lush';
  else if (vibe.density <= 0.25) toneWord = 'sparse';

  // Pick a descriptor from the most expressive node types
  const preferredTypes = ['genre', 'mood', 'texture', 'scene'] as const;
  const descriptorNode = nodes.find((n) => (preferredTypes as readonly string[]).includes(n.type));

  const parts: string[] = [];
  if (toneWord) parts.push(toneWord);
  if (descriptorNode) {
    // Use the full value if short (≤2 words), otherwise just the first word
    const words = descriptorNode.value.toLowerCase().split(/\s+/);
    parts.push(words.length <= 2 ? words.join(' ') : words[0]);
  } else if (vibe.obscurity >= 0.7) {
    parts.push('rare');
  }
  parts.push('constellation');
  return parts.join(' ');
}

export default function ExportPlaylistModal() {
  const { vibe, nodes } = useExploreStore();
  const {
    isModalOpen,
    pendingTracks,
    connectedPlatforms,
    isLoadingPlatforms,
    isExporting,
    isConnectingApple,
    exportResult,
    closeModal,
    connectAppleMusic,
    disconnectPlatform,
    exportPlaylist,
  } = usePlaylistExportStore();

  const [playlistName, setPlaylistName] = useState(() => generatePlaylistName(vibe, nodes));

  if (!isModalOpen) return null;

  const isAppleConnected = connectedPlatforms.some((p) => p.platform === 'apple_music');
  const appleConn = connectedPlatforms.find((p) => p.platform === 'apple_music');

  const handleExport = () => {
    if (!playlistName.trim()) return;
    exportPlaylist('apple_music', playlistName.trim());
  };

  const canExport = isAppleConnected && playlistName.trim().length > 0 && !isExporting;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={closeModal}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Export to Playlist</h2>
          <button
            onClick={closeModal}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {exportResult ? (
            // ── Success State ──
            <div className="text-center py-4 space-y-4">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <svg className="w-7 h-7 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <p className="font-semibold text-gray-900">Playlist created!</p>
                <p className="text-sm text-gray-500 mt-1">
                  {exportResult.tracksAdded} track{exportResult.tracksAdded !== 1 ? 's' : ''} added
                  {exportResult.tracksSkipped > 0 && `, ${exportResult.tracksSkipped} not found`}
                </p>
              </div>
              <a
                href={exportResult.playlistUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                Open Playlist
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
              <button
                onClick={closeModal}
                className="block w-full text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              {/* Track count */}
              <p className="text-sm text-gray-600">
                Exporting <span className="font-medium text-gray-900">{pendingTracks.length}</span> tracks
              </p>

              {/* Platform */}
              <div className="space-y-2">
                {isLoadingPlatforms ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                    <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                    Loading...
                  </div>
                ) : (
                  <PlatformCard
                    platform="apple_music"
                    label="Apple Music"
                    icon={<AppleMusicIcon />}
                    color="bg-[#FC3C44]"
                    isConnected={isAppleConnected}
                    displayName={appleConn?.displayName ?? null}
                    onConnect={connectAppleMusic}
                    onDisconnect={() => disconnectPlatform('apple_music')}
                    isConnecting={isConnectingApple}
                  />
                )}
              </div>

              {/* Playlist name */}
              <div className="space-y-1.5">
                <label htmlFor="playlist-name" className="text-sm font-medium text-gray-700">
                  Playlist name
                </label>
                <input
                  id="playlist-name"
                  type="text"
                  value={playlistName}
                  onChange={(e) => setPlaylistName(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  maxLength={200}
                  placeholder="My Playlist"
                />
              </div>

              {/* Export button */}
              <button
                onClick={handleExport}
                disabled={!canExport}
                className="w-full px-4 py-3 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {isExporting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Creating playlist...
                  </>
                ) : (
                  'Export Playlist'
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface PlatformCardProps {
  platform: string;
  label: string;
  icon: React.ReactNode;
  color: string;
  isConnected: boolean;
  displayName: string | null;
  onConnect: () => void;
  onDisconnect: () => void;
  isConnecting?: boolean;
}

function PlatformCard({
  label,
  icon,
  color,
  isConnected,
  displayName,
  onConnect,
  onDisconnect,
  isConnecting,
}: PlatformCardProps) {
  return (
    <div
      className={`flex items-center justify-between p-3 rounded-lg border-2 ${
        isConnected ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-gray-50'
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 ${color} rounded-lg flex items-center justify-center text-white flex-shrink-0`}>
          {icon}
        </div>
        <div>
          <p className="text-sm font-medium text-gray-900">{label}</p>
          {isConnected && displayName && (
            <p className="text-xs text-gray-500">{displayName}</p>
          )}
          {isConnected && (
            <p className="text-xs text-green-600 font-medium">Connected</p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        {isConnected ? (
          <button
            onClick={onDisconnect}
            className="text-xs text-gray-400 hover:text-red-500 transition-colors px-1"
          >
            Disconnect
          </button>
        ) : (
          <button
            onClick={onConnect}
            disabled={isConnecting}
            className="px-3 py-1.5 text-xs font-medium bg-gray-900 text-white rounded-md hover:bg-gray-700 disabled:opacity-50 transition-colors flex items-center gap-1.5"
          >
            {isConnecting ? (
              <>
                <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Connecting...
              </>
            ) : (
              'Connect'
            )}
          </button>
        )}
      </div>
    </div>
  );
}

function AppleMusicIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M23.994 6.124a9.23 9.23 0 00-.24-2.19c-.317-1.31-1.048-2.31-2.17-3.04a5.022 5.022 0 00-1.76-.69c-.51-.098-1.03-.144-1.54-.15-.04-.001-4.35-.002-4.35-.002s-4.3 0-4.34.002c-.51.006-1.03.052-1.54.15a5.022 5.022 0 00-1.76.69C5.282 1.624 4.55 2.624 4.234 3.934A9.224 9.224 0 003.994 6.124C3.99 6.164 3.99 10.47 3.99 10.47s0 4.304.004 4.345c.038 1.072.24 2.124.706 3.073.754 1.536 2.05 2.5 3.718 2.858.508.106 1.03.155 1.546.16.04.001 4.344.002 4.344.002s4.304 0 4.345-.002c.516-.005 1.038-.054 1.546-.16 1.668-.358 2.964-1.322 3.718-2.858.466-.95.668-2.001.706-3.073.004-.041.004-4.345.004-4.345s0-4.306-.004-4.346zM16.49 12.64l-4.16 2.4a.652.652 0 01-.988-.558V9.518a.652.652 0 01.988-.558l4.16 2.4a.652.652 0 010 1.28z"/>
    </svg>
  );
}
